import { useEffect, useState } from 'react'
import Client from '../../../../../client'
import { DepopSpecific, DepopSpecificValue } from '../types'
import { getErrorString } from '../../../../../utils/serverErrorUtils'

interface DepopCategorySpecifics {
  specifics: DepopSpecific[]
  valuesByName: Record<string, DepopSpecificValue[]>
  loading: boolean
  error: string
}

// Unlike Depop's category-agnostic global specifics, the per-product-type specifics change with
// the selected category, so this hook refetches whenever `categoryMarketCategoryId` changes. The
// per-category endpoint omits value recommendations, so values are fetched per specific the same
// way the global hook does.
export const useDepopCategorySpecifics = (categoryMarketCategoryId?: number): DepopCategorySpecifics => {
  const [specifics, setSpecifics] = useState<DepopSpecific[]>([])
  const [valuesByName, setValuesByName] = useState<Record<string, DepopSpecificValue[]>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!categoryMarketCategoryId) {
      setSpecifics([])
      setValuesByName({})
      setLoading(false)
      setError('')
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError('')

    const fetchCategorySpecifics = async () => {
      const res = await Client.depopCategorySpecifics(categoryMarketCategoryId)
      const categorySpecifics = res.data.specifics || []

      const valueResponses = await Promise.all(
        categorySpecifics.map(s =>
          Client.depopSpecificValues(s.name).then(r => [s.name, r.data.valueRecommendations] as const)
        )
      )

      return {
        specifics: categorySpecifics,
        valuesByName: valueResponses.reduce(
          (acc, [name, values]) => ({ ...acc, [name]: values }),
          {} as Record<string, DepopSpecificValue[]>
        )
      }
    }

    fetchCategorySpecifics()
      .then(data => {
        if (cancelled) return
        setSpecifics(data.specifics)
        setValuesByName(data.valuesByName)
        setError('')
      })
      .catch(e => {
        if (cancelled) return
        setSpecifics([])
        setValuesByName({})
        setError(getErrorString(e) || 'Failed to load Depop category specifics')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [categoryMarketCategoryId])

  return { specifics, valuesByName, loading, error }
}
